<template>
    <div class="table-container w-full" data-cy="company-table-container">
        <div class="mt-1 flex justify-between">
            <div class="w-auto">
                <a-input-search
                    data-cy="input-search"
                    @search="onSearch"
                    :placeholder="$t('association.companies.table.top_actions.search')"
                />
            </div>
            <div class="w-1/4" v-if="isAdmin" data-cy="admin-settings-association-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    :filter-option="false"
                    :not-found-content="null"
                    @search="handleFilterSearch"
                    @change="handleFilterChange"
                    :allowClear="true"
                >
                    <a-select-opt-group
                        v-if="
                            association_list.some((value) =>
                                value.name.toLowerCase().includes(filter_value.toLowerCase()),
                            )
                        "
                        label="Association"
                    >
                        <template v-for="(value, index) in association_list">
                            <a-select-option
                                v-if="value.name.toLowerCase().includes(filter_value.toLowerCase())"
                                :key="`${index}-association`"
                                :value="`${index}-association`"
                            >
                                {{ value.name }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                    <a-select-opt-group
                        v-if="
                            company_types.some((value) => value.text.toLowerCase().includes(filter_value.toLowerCase()))
                        "
                        label="Company Type"
                    >
                        <template v-for="(value, index) in company_types">
                            <a-select-option
                                v-if="value.text.toLowerCase().includes(filter_value.toLowerCase())"
                                :key="`${index}-companyType`"
                                :value="`${index}-companyType`"
                            >
                                {{ value.text }}
                            </a-select-option>
                        </template>
                    </a-select-opt-group>
                </a-select>
            </div>
            <div class="w-1/4" v-if="!isAdmin" data-cy="company-settings-company-type-filter">
                <a-select
                    show-search
                    :placeholder="$t('globals.tables.filter')"
                    style="width: 100%"
                    option-filter-prop="children"
                    :filter-option="filterOption"
                    @change="handleCompanyTypeFilterChange"
                    :allowClear="true"
                >
                    <a-select-option v-for="(value, index) in company_types" :key="index" :value="index">
                        {{ value.text }}
                    </a-select-option>
                </a-select>
            </div>
            <div v-if="showAddCompany">
                <a-button-group>
                    <a-button data-cy="create-company-button" type="primary" @click="onAdd">
                        {{ $t('association.companies.table.top_actions.create') }}
                    </a-button>
                </a-button-group>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="company-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('association.companies.table.top_actions.edit')"
                                    v-if="isAdmin"
                                >
                                    <a-button
                                        data-cy="edit-button"
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'edit')"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('association.companies.table.top_actions.view')"
                                    v-if="!isAdmin"
                                >
                                    <a-button
                                        data-cy="view-button"
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'view')"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip
                                    placement="top"
                                    :title="$t('association.companies.table.top_actions.update')"
                                >
                                    <a-button
                                        data-cy="update-date-button"
                                        class="text-default-gray hover:text-default-gray focus:text-default-gray"
                                        @click="updateDate(record.id)"
                                    >
                                        <CalendarOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'group_code'">
                            <span v-if="record.is_data_provider === 1" class="font-semibold">
                                {{ $t('company.table.columns.is_data_provider') }}
                            </span>
                            <span v-if="record.is_data_provider === 1 && record.is_api_access === 1">/</span>
                            <span v-if="record.is_api_access === 1" class="font-semibold">
                                {{ $t('company.table.columns.is_api_access') }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'is_active'">
                            <span class="font-semibold text-default-red" v-if="text === 'inactive'">
                                {{ $t('company.table.columns.inactive') }}
                            </span>
                            <span class="font-semibold text-default-green" v-if="text === 'active'">
                                {{ $t('company.table.columns.active') }}
                            </span>
                            <span class="font-semibold text-default-orange" v-if="text === 'declined'">
                                {{ $t('company.table.columns.declined') }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'claim_last_imported_at'">
                            {{ displayDateTime(text) }}
                        </template>
                        <template v-else-if="column.key === 'cover_last_imported_at'">
                            {{ displayDateTime(text) }}
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import User from '@/models/user.js';
import { CalendarOutlined, EditOutlined } from '@ant-design/icons-vue';

export default {
    components: { CalendarOutlined, EditOutlined },
    data() {
        return {
            data: [],
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            filter_value: '',
            association_list: [],
            company_types: [],
            search_value: null,
            filter_association_id: null,
            filter_company_type: null,
        };
    },
    props: {
        association_id: String,
        is_admin_section: Boolean,
        active_companies_only: {
            type: Boolean,
            default: true,
        },
    },
    mounted() {
        this.filter_association_id = this.association_id;

        this.fetch({
            active_companies_only: this.active_companies_only,
        });

        if (this.isAdmin) {
            this.$http
                .get('/api/admin/list-associations')
                .then((response) => {
                    this.association_list = response.data.data;
                })
                .catch((error) => {
                    this.$notification.error({
                        message: this.$t('globals.global.notifications.error.any', { code: 'CVAL01' }),
                    });
                });
        }
        this.$http.get('/api/listmanager/category/company_type').then((response) => {
            response.data.data.forEach((record) => {
                this.company_types.push({
                    text: record.item,
                    value: record.value,
                });
            });
        });
    },
    computed: {
        getColumns() {
            let columns = [
                {
                    key: 'actions',
                    width: 35,
                },
                {
                    title: this.$t('globals.tables.companies.headers.company_id'),
                    key: 'company_id',
                    dataIndex: 'company_id',
                    sorter: true,
                },
                {
                    key: 'group_code',
                    title: this.$t('globals.tables.companies.headers.attribute'),
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.name'),
                    key: 'name',
                    dataIndex: 'name',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.company_type'),
                    key: 'company_type',
                    dataIndex: 'company_type',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.company_code'),
                    key: 'company_code',
                    dataIndex: 'company_code',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.status'),
                    key: 'is_active',
                    dataIndex: 'is_active',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.user_count'),
                    key: 'user_count',
                    dataIndex: 'user_count',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.claim_last_imported_at'),
                    key: 'claim_last_imported_at',
                    dataIndex: 'claim_last_imported_at',
                    sorter: true,
                },
                {
                    title: this.$t('globals.tables.companies.headers.cover_last_imported_at'),
                    key: 'cover_last_imported_at',
                    dataIndex: 'cover_last_imported_at',
                    sorter: true,
                },
            ];

            if (this.isAdmin) {
                columns.splice(1, 0, {
                    title: this.$t('globals.tables.companies.headers.association_code'),
                    key: 'association_code',
                    dataIndex: 'association_code',
                    sorter: true,
                });
            }

            return columns;
        },
        isAdmin() {
            return this.is_admin_section ? true : false;
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;
            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        showAddCompany() {
            return User.can('view-association');
        },
    },
    methods: {
        displayDateTime(datetime) {
            return datetime != null
                ? this.$dayjs(datetime).format('YYYY-MM-DD HH:mm:ss')
                : this.$t('globals.tables.date_null');
        },
        handleTableChange(pagination, filters, sorter) {
            const pager = { ...this.pagination };

            pager.current = pagination.current;

            this.pagination = pager;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        fetch(params = {}) {
            this.loading = true;

            if (params.search_value == undefined || params.search_value == null) {
                params.search_value = this.search_value;
            }

            if (params.association_id == undefined || params.association_id == null) {
                params.association_id = this.filter_association_id;
            }

            if (params.company_type == undefined || params.company_type == null) {
                params.company_type = this.filter_company_type;
            }

            if (params.hasOwnProperty('active_companies_only')) {
                params.active_companies_only = params.active_companies_only ? params.active_companies_only : null;
            } else {
                params.active_companies_only = this.active_companies_only ? this.active_companies_only : null;
            }

            this.params = {
                ...params,
            };

            if (this.isAdmin) {
                if (params.association_id == null) {
                    this.$http
                        .get('/api/companies', {
                            params: {
                                ...this.params,
                            },
                        })
                        .then((response) => {
                            const pagination = { ...this.pagination };

                            // Read total count from server
                            pagination.total = response.data.meta.total_count;

                            this.loading = false;
                            this.data = response.data.data;
                            this.pagination = pagination;
                        })
                        .catch((error) => {
                            if (error.response.status == 403) {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.403_mod'),
                                });
                            } else {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.any', { code: 'CVCF01' }),
                                });
                            }
                        });
                } else {
                    this.$http
                        .get('/api/associations/' + params.association_id + '/companies', {
                            params: {
                                ...this.params,
                            },
                        })
                        .then((response) => {
                            const pagination = { ...this.pagination };

                            // Read total count from server
                            pagination.total = response.data.meta.total_count;

                            this.loading = false;
                            this.data = response.data.data;
                            this.pagination = pagination;
                        })
                        .catch((error) => {
                            if (error.response.status == 403) {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.403_mod'),
                                });
                            } else {
                                this.$notification.error({
                                    message: this.$t('globals.global.notifications.error.any', { code: 'CVCF02' }),
                                });
                            }
                        });
                }
            } else {
                delete this.params.association_id;

                this.$http
                    .get('/api/associations/' + this.association_id + '/companies', {
                        params: {
                            ...this.params,
                        },
                    })
                    .then((response) => {
                        const pagination = { ...this.pagination };

                        // Read total count from server
                        pagination.total = response.data.meta.total_count;

                        this.loading = false;
                        this.data = response.data.data;
                        this.pagination = pagination;
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVCF03' }),
                            });
                        }
                    });
            }
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.fetch({
                    search_value: this.search_value,
                    company_type: this.filter_company_type,
                    association_id: this.filter_association_id,
                });
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        handleFilterSearch(value) {
            this.filter_value = value;
        },
        handleCompanyTypeFilterChange(value) {
            this.filter_company_type = null;

            if (value != undefined) {
                this.filter_company_type = this.company_types[value].value;
            }
            this.fetch({
                company_type: this.filter_company_type,
                association_id: this.filter_association_id,
                search_value: this.search_value,
            });
            this.pagination.current = 1;
        },
        handleFilterChange(value) {
            this.filter_association_id = null;
            this.filter_company_type = null;

            if (value != undefined) {
                if (value.includes('association')) {
                    this.filter_association_id = this.association_list[parseInt(value)].id;
                } else {
                    this.filter_company_type = this.company_types[parseInt(value)].value;
                }
            }

            this.fetch({
                company_type: this.filter_company_type,
                association_id: this.filter_association_id,
                search_value: this.search_value,
            });
            this.filter_value = '';
            this.pagination.current = 1;
        },
        filterOption(input, option) {
            return option.children()[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        onAdd() {
            this.$emit('onAdd');
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        updateDate(id) {
            this.$emit('updateDate', id);
        },
        onTableAction(id, key) {
            this.$emit('onTableAction', {
                id: id,
                key: key,
            });
        },
    },
};
</script>
